import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { getHobbyMediaStyle, HobbyMediaIcon } from '../../content/hobbyMedia';
import {
  externalSharePreferenceOptions,
  resolveExternalSharingPreferences,
  shareHighlightStrings as strings,
} from '../../content/shareHighlights';
import { GET_MY_PROFILE, UPDATE_MY_PROFILE } from '../../views/account/operations';
import { CameraIcon } from '../common/ActionIcons';
import AccessibleModal from '../common/AccessibleModal';
import { fitShareTitle, postMediaHeight, wrapShareText } from '../../features/shareHighlights/shareHighlightLayout';

const FORMATS = {
  post: { width: 1080, height: 1350 },
  story: { width: 1080, height: 1920 },
};

/**
 * Performs the supports native file sharing operation.
 *
 * @returns {*} The derived operation result.
 */
function supportsNativeFileSharing() {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [new File([''], 'hobtalk-share.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
}

/**
 * Performs the rounded rect operation.
 *
 * @param {*} context - context value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @param {*} radius - radius value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function roundedRect(context, x, y, width, height, radius) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

/**
 * Performs the fit text operation.
 *
 * @param {*} context - context value.
 * @param {*} text - text value.
 * @param {*} maxWidth - max width value.
 * @param {number} initialSize - initial size numeric value.
 * @param {number} minimumSize - minimum size numeric value.
 * @param {number} weight - weight value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function fitText(context, text, maxWidth, initialSize, minimumSize, weight = 700) {
  let size = initialSize;
  do {
    context.font = `${weight} ${size}px system-ui, sans-serif`;
    if (context.measureText(text).width <= maxWidth) return;
    size -= 1;
  } while (size >= minimumSize);
}

const DEFAULT_ADJUSTMENT = Object.freeze({ zoom: 1, horizontal: 0, vertical: 0 });

/**
 * Performs the filter highlight for sharing operation.
 *
 * @param {*} highlight - highlight value.
 * @param {*} preferences - preferences value.
 * @param {string} displayName - display name value.
 * @returns {*} The derived operation result.
 */
function filterHighlightForSharing(highlight, preferences, displayName) {
  let metrics = [...(highlight.metrics || [])];
  if (!preferences.includeRatings) metrics = metrics.filter((item) => item.label.toLowerCase() !== 'rating');
  if (!preferences.showExactDates) metrics = metrics.filter((item) => !['date', 'dates'].includes(item.label.toLowerCase()));
  if (!preferences.includeDetailedMetrics) {
    const preferred = highlight.heroMetric && metrics.find((item) => item.label === highlight.heroMetric.label);
    metrics = preferred ? [preferred] : metrics.slice(0, 1);
  }

  let title = highlight.title;
  let subtitle = highlight.subtitle;
  if (!preferences.showPlaceNames && highlight.hobby === 'traveling') {
    title = 'Trip highlight';
    subtitle = '';
  }
  if (!preferences.showPlaceNames && highlight.hobby === 'hiking') {
    title = 'Hiking highlight';
    subtitle = '';
  }
  if (!preferences.showExactDates && highlight.subtitleWithoutDate !== undefined) subtitle = highlight.subtitleWithoutDate;

  return {
    ...highlight,
    title,
    subtitle,
    mediaUrl: preferences.useEntryPhotos ? highlight.mediaUrl : '',
    metrics,
    heroMetric: metrics.find((item) => item.label === highlight.heroMetric?.label) || metrics[0] || null,
    review: preferences.includeReviews ? highlight.review : '',
    displayName: preferences.showDisplayName ? displayName : '',
  };
}

/**
 * Performs the draw image cover operation.
 *
 * @param {*} context - context value.
 * @param {*} image - image value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @param {*} adjustment - adjustment value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function drawImageCover(context, image, x, y, width, height, adjustment = DEFAULT_ADJUSTMENT) {
  const scale = Math.max(width / image.width, height / image.height) * adjustment.zoom;
  const drawnWidth = image.width * scale;
  const drawnHeight = image.height * scale;
  const horizontalOverflow = Math.max(0, drawnWidth - width);
  const verticalOverflow = Math.max(0, drawnHeight - height);
  const drawX = x + (width - drawnWidth) / 2 - adjustment.horizontal * horizontalOverflow / 2;
  const drawY = y + (height - drawnHeight) / 2 - adjustment.vertical * verticalOverflow / 2;
  context.drawImage(image, drawX, drawY, drawnWidth, drawnHeight);
}

/**
 * Performs the draw image contain operation.
 *
 * @param {*} context - context value.
 * @param {*} image - image value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function drawImageContain(context, image, x, y, width, height) {
  const scale = Math.min(width / image.width, height / image.height);
  const drawnWidth = image.width * scale;
  const drawnHeight = image.height * scale;
  context.drawImage(image, x + (width - drawnWidth) / 2, y + (height - drawnHeight) / 2, drawnWidth, drawnHeight);
}

/**
 * Performs the draw framed image operation.
 *
 * @param {*} context - context value.
 * @param {*} image - image value.
 * @param {*} presentation - presentation value.
 * @param {*} x - x value.
 * @param {*} y - y value.
 * @param {*} width - width value.
 * @param {*} height - height value.
 * @param {*} adjustment - adjustment value.
 * @param {*} usesPhotoCrop - uses photo crop value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
function drawFramedImage(context, image, presentation, x, y, width, height, adjustment, usesPhotoCrop) {
  if (usesPhotoCrop) {
    drawImageCover(context, image, x, y, width, height, adjustment);
    return;
  }
  if (presentation !== 'portrait') {
    drawImageCover(context, image, x, y, width, height);
    return;
  }
  context.save();
  context.filter = 'blur(24px)';
  context.globalAlpha = 0.42;
  drawImageCover(context, image, x - 18, y - 18, width + 36, height + 36);
  context.restore();
  drawImageContain(context, image, x, y, width, height);
}

/**
 * Performs the canvas blob operation.
 *
 * @param {boolean} canvas - Whether canvas is enabled.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function canvasBlob(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Unable to render image')), 'image/png'));
}

/**
 * Retrieves image.
 *
 * @param {string} url - URL used by the operation.
 * @returns {*} The derived operation result.
 */
function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/** Draw the same filled and half-filled stars into the exported canvas image. */
function drawRatingStars(context, rating, x, baseline, size) {
  context.save();
  context.font = `700 ${size}px system-ui, sans-serif`;
  const starWidth = context.measureText('★').width;
  const step = starWidth + size * 0.08;
  for (let index = 0; index < 5; index += 1) {
    const starX = x + index * step;
    const fill = Math.min(1, Math.max(0, rating - index));
    context.fillStyle = '#697687';
    context.fillText('★', starX, baseline);
    if (fill > 0) {
      context.save();
      context.beginPath();
      context.rect(starX, baseline - size, starWidth * fill, size * 1.2);
      context.clip();
      context.fillStyle = '#ffd65a';
      context.fillText('★', starX, baseline);
      context.restore();
    }
  }
  context.restore();
}

/**
 * Renders highlight.
 *
 * @param {*} highlight - highlight value.
 * @param {*} format - format value.
 * @param {*} includeReview - include review value.
 * @param {*} mediaState - media state value.
 * @param {*} adjustment - adjustment value.
 * @returns {Promise<*>} A promise resolving to the completed operation result.
 */
async function renderHighlight(highlight, format, includeReview, mediaState, adjustment) {
  const dimensions = FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  const context = canvas.getContext('2d');
  const style = getHobbyMediaStyle(highlight.hobby);
  const media = await loadImage(mediaState.url);
  const logo = await loadImage('/assets/brand/hobtalk-mark-transparent-192.png');

  if (format === 'story') {
    context.fillStyle = '#070b14';
    context.fillRect(0, 0, dimensions.width, dimensions.height);

    if (media) {
      context.save();
      context.filter = 'blur(34px)';
      context.globalAlpha = 0.5;
      drawImageCover(context, media, -35, -35, dimensions.width + 70, dimensions.height + 70, adjustment);
      context.restore();
      if (mediaState.source !== 'fallback') drawImageCover(context, media, 0, 0, dimensions.width, dimensions.height, adjustment);
      else drawImageContain(context, media, 0, 0, dimensions.width, dimensions.height);
    } else {
      const fallback = context.createLinearGradient(0, 0, dimensions.width, dimensions.height);
      fallback.addColorStop(0, `${style.accent}44`);
      fallback.addColorStop(0.55, '#111b28');
      fallback.addColorStop(1, '#070b14');
      context.fillStyle = fallback;
      context.fillRect(0, 0, dimensions.width, dimensions.height);
      context.strokeStyle = `${style.accent}99`;
      context.lineWidth = 18;
      context.beginPath();
      context.arc(dimensions.width / 2, dimensions.height * 0.31, 116, 0, Math.PI * 2);
      context.stroke();
      context.fillStyle = style.accent;
      context.font = '850 112px system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(style.label.slice(0, 1), dimensions.width / 2, dimensions.height * 0.31);
    }

    const shade = context.createLinearGradient(0, 0, 0, dimensions.height);
    shade.addColorStop(0, '#070b1408');
    shade.addColorStop(0.4, '#070b1448');
    shade.addColorStop(0.68, '#070b14ef');
    shade.addColorStop(1, '#070b14');
    context.fillStyle = shade;
    context.fillRect(0, 0, dimensions.width, dimensions.height);

    const padding = 78;
    const heroMetric = highlight.heroMetric;
    const supportingMetrics = (highlight.metrics || []).filter((item) => !heroMetric || item.label !== heroMetric.label).slice(0, 2);
    const title = fitShareTitle(context, highlight.title, dimensions.width - padding * 2, 70, 44, 4);
    const titleLineHeight = Math.round(title.size * 1.12);
    context.font = '650 31px system-ui, sans-serif';
    const subtitleLines = highlight.subtitle ? wrapShareText(context, highlight.subtitle, dimensions.width - padding * 2, 1) : [];
    context.font = '550 28px system-ui, sans-serif';
    const reviewLines = includeReview && highlight.review ? wrapShareText(context, `“${highlight.review}”`, dimensions.width - padding * 2, 2) : [];
    const contentHeight = 62 + title.lines.length * titleLineHeight + subtitleLines.length * 42
      + (heroMetric ? 136 : 0) + (supportingMetrics.length ? 112 : 0) + reviewLines.length * 40;
    let y = Math.max(0, dimensions.height - 165 - contentHeight);

    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    context.fillStyle = style.accent;
    context.font = '850 32px system-ui, sans-serif';
    context.fillText(cleanLabel(highlight.headline || style.label).toUpperCase(), padding, y);
    y += 62;
    context.fillStyle = '#f7fbff';
    context.font = `850 ${title.size}px system-ui, sans-serif`;
    title.lines.forEach((line) => { context.fillText(line, padding, y); y += titleLineHeight; });
    if (subtitleLines.length) {
      context.fillStyle = '#c1ccd8';
      context.font = '650 31px system-ui, sans-serif';
      subtitleLines.forEach((line) => { context.fillText(line, padding, y); y += 42; });
    }
    if (heroMetric) {
      y += 20;
      context.fillStyle = style.accent;
      if (heroMetric.rating != null) {
        drawRatingStars(context, heroMetric.rating, padding, y + 52, 66);
      } else {
        fitText(context, heroMetric.value, dimensions.width - padding * 2, 66, 42, 850);
        context.fillText(heroMetric.value, padding, y + 52);
      }
      context.fillStyle = '#9aa9b8';
      context.font = '800 22px system-ui, sans-serif';
      context.fillText(heroMetric.label.toUpperCase(), padding, y + 86);
      y += 116;
    }
    if (supportingMetrics.length) {
      const gap = 18;
      const boxWidth = (dimensions.width - padding * 2 - gap * (supportingMetrics.length - 1)) / supportingMetrics.length;
      supportingMetrics.forEach((item, index) => {
        const x = padding + index * (boxWidth + gap);
        roundedRect(context, x, y, boxWidth, 92, 20);
        context.fillStyle = '#070b1473';
        context.fill();
        context.strokeStyle = '#ffffff1f';
        context.lineWidth = 2;
        context.stroke();
        context.fillStyle = '#9aa9b8';
        context.font = '800 20px system-ui, sans-serif';
        context.fillText(item.label.toUpperCase(), x + 20, y + 32);
        context.fillStyle = '#f7fbff';
        if (item.rating != null) drawRatingStars(context, item.rating, x + 20, y + 69, 28);
        else {
          fitText(context, item.value, boxWidth - 40, 28, 20);
          context.fillText(item.value, x + 20, y + 69);
        }
      });
      y += 112;
    }
    if (reviewLines.length) {
      context.fillStyle = '#d5dee8';
      context.font = '550 28px system-ui, sans-serif';
      reviewLines.forEach((line) => { context.fillText(line, padding, y); y += 40; });
    }

    const footerY = dimensions.height - 76;
    if (logo) context.drawImage(logo, padding, footerY - 38, 48, 48);
    context.fillStyle = '#f7fbff';
    context.font = '700 25px system-ui, sans-serif';
    context.fillText(highlight.displayName ? `${strings.footer} · ${highlight.displayName}` : strings.footer, padding + 62, footerY - 5);
    context.fillStyle = '#a4b2c1';
    context.font = '650 23px system-ui, sans-serif';
    context.textAlign = 'right';
    context.fillText(strings.website, dimensions.width - padding, footerY - 5);
    return canvasBlob(canvas);
  }

  context.fillStyle = '#070b14';
  context.fillRect(0, 0, dimensions.width, dimensions.height);
  const glow = context.createRadialGradient(180, 130, 10, 180, 130, 760);
  glow.addColorStop(0, `${style.accent}55`);
  glow.addColorStop(1, '#070b1400');
  context.fillStyle = glow;
  context.fillRect(0, 0, dimensions.width, dimensions.height);

  const isStory = format === 'story';
  const padding = isStory ? 78 : 72;
  const title = fitShareTitle(context, highlight.title, dimensions.width - padding * 2, 61, 43, 4);
  const titleLineHeight = Math.round(title.size * 1.12);
  context.font = '600 29px system-ui, sans-serif';
  const subtitleLines = highlight.subtitle ? wrapShareText(context, highlight.subtitle, dimensions.width - padding * 2, 1) : [];
  context.font = '500 30px system-ui, sans-serif';
  const reviewLines = includeReview && highlight.review ? wrapShareText(context, `“${highlight.review}”`, dimensions.width - padding * 2, 2) : [];
  const visibleMetricCount = Math.min((highlight.metrics || []).length, 4);
  const mediaHeight = postMediaHeight({
    height: dimensions.height, padding, hasMedia: Boolean(media),
    titleLines: title.lines.length, titleLineHeight,
    subtitleLines: subtitleLines.length, metricCount: visibleMetricCount,
    reviewLines: reviewLines.length,
  });
  roundedRect(context, padding, padding, dimensions.width - padding * 2, mediaHeight, 42);
  context.save();
  context.clip();
  if (media) {
    drawFramedImage(context, media, highlight.mediaPresentation, padding, padding, dimensions.width - padding * 2, mediaHeight, adjustment, mediaState.source !== 'fallback');
    const shade = context.createLinearGradient(0, padding, 0, padding + mediaHeight);
    shade.addColorStop(0.45, '#070b1400');
    shade.addColorStop(1, '#070b14cc');
    context.fillStyle = shade;
    context.fillRect(padding, padding, dimensions.width - padding * 2, mediaHeight);
  } else {
    context.fillStyle = `${style.accent}22`;
    context.fillRect(padding, padding, dimensions.width - padding * 2, mediaHeight);
    context.strokeStyle = `${style.accent}88`;
    context.lineWidth = 12;
    context.beginPath();
    context.arc(dimensions.width / 2, padding + mediaHeight / 2, 76, 0, Math.PI * 2);
    context.stroke();
    context.fillStyle = style.accent;
    context.font = '800 72px system-ui, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(style.label.slice(0, 1), dimensions.width / 2, padding + mediaHeight / 2);
  }
  context.restore();

  let y = padding + mediaHeight + (isStory ? 70 : 42);
  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillStyle = style.accent;
  context.font = `800 ${isStory ? 34 : 27}px system-ui, sans-serif`;
  context.fillText(cleanLabel(highlight.headline || style.label).toUpperCase(), padding, y);
  y += isStory ? 74 : 54;
  context.fillStyle = '#f7fbff';
  context.font = `850 ${title.size}px system-ui, sans-serif`;
  title.lines.forEach((line) => {
    context.fillText(line, padding, y);
    y += titleLineHeight;
  });
  if (subtitleLines.length) {
    context.fillStyle = '#aebbc9';
    context.font = `600 ${isStory ? 34 : 29}px system-ui, sans-serif`;
    subtitleLines.forEach((line) => {
      context.fillText(line, padding, y);
      y += isStory ? 46 : 39;
    });
  }

  const heroMetric = highlight.heroMetric;
  const supportingMetrics = (highlight.metrics || []).filter((item) => !heroMetric || item.label !== heroMetric.label);
  if (isStory && heroMetric) {
    y += 34;
    context.fillStyle = style.accent;
    context.font = '850 68px system-ui, sans-serif';
    if (heroMetric.rating != null) drawRatingStars(context, heroMetric.rating, padding, y, 68);
    else context.fillText(heroMetric.value, padding, y);
    y += 36;
    context.fillStyle = '#8797a8';
    context.font = '800 23px system-ui, sans-serif';
    context.fillText(heroMetric.label.toUpperCase(), padding, y);
    y += 22;
  }

  const visibleMetrics = isStory ? supportingMetrics.slice(0, 2) : (highlight.metrics || []).slice(0, 4);
  if (visibleMetrics.length) {
    y += isStory ? 24 : 20;
    const gap = 16;
    const columns = Math.min(visibleMetrics.length, 2);
    const boxWidth = (dimensions.width - padding * 2 - gap * (columns - 1)) / columns;
    visibleMetrics.forEach((item, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = padding + column * (boxWidth + gap);
      const boxY = y + row * 116;
      roundedRect(context, x, boxY, boxWidth, 98, 22);
      context.fillStyle = '#111b28';
      context.fill();
      context.fillStyle = '#8797a8';
      context.font = '700 22px system-ui, sans-serif';
      context.fillText(item.label.toUpperCase(), x + 22, boxY + 34);
      context.fillStyle = '#f7fbff';
      if (item.rating != null) drawRatingStars(context, item.rating, x + 22, boxY + 72, 29);
      else {
        fitText(context, item.value, boxWidth - 44, 29, 20);
        context.fillText(item.value, x + 22, boxY + 72);
      }
    });
    y += Math.ceil(visibleMetrics.length / columns) * 116 + 12;
  }

  if (reviewLines.length) {
    context.fillStyle = '#d5dee8';
    context.font = '500 30px system-ui, sans-serif';
    reviewLines.forEach((line) => {
      context.fillText(line, padding, y);
      y += 42;
    });
  }

  const footerY = dimensions.height - 76;
  if (logo) context.drawImage(logo, padding, footerY - 38, 48, 48);
  context.fillStyle = '#f7fbff';
  context.font = '700 27px system-ui, sans-serif';
  context.fillText(highlight.displayName ? `${strings.footer} · ${highlight.displayName}` : strings.footer, padding + 62, footerY - 5);
  context.fillStyle = '#8797a8';
  context.font = '600 24px system-ui, sans-serif';
  context.textAlign = 'right';
  context.fillText(strings.website, dimensions.width - padding, footerY - 5);

  return canvasBlob(canvas);
}

/**
 * Normalizes label.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function cleanLabel(value) {
  return String(value || '').trim();
}

/** Preview the actual exported composition, including its final text wrapping. */
function ShareHighlightPreview({ highlight, format, includeReview, mediaState, adjustment, style, onPhotoTap }) {
  const [preview, setPreview] = useState(null);
  const activeUrl = useRef(null);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const blob = await renderHighlight(highlight, format, includeReview, mediaState, adjustment);
        if (!active) return;
        const url = URL.createObjectURL(blob);
        const previous = activeUrl.current;
        activeUrl.current = url;
        setPreview({ format, url });
        if (previous) URL.revokeObjectURL(previous);
      } catch {
        if (active) setPreview(null);
      }
    }, 80);
    return () => { active = false; clearTimeout(timer); };
  }, [highlight, format, includeReview, mediaState.source, mediaState.url, adjustment]);

  useEffect(() => () => {
    if (activeUrl.current) URL.revokeObjectURL(activeUrl.current);
  }, []);

  return <div className={`share-highlight-preview share-highlight-${format}`} style={{ '--share-highlight-accent': style.accent }}>
    {preview?.format === format
      ? <img className="share-highlight-preview-image" src={preview.url} alt={`${highlight.title} share highlight preview`} />
      : <HobbyMediaIcon type={highlight.hobby} />}
    <button type="button" className="share-highlight-photo-overlay" aria-label={mediaState.source === 'fallback' ? strings.addPhoto : strings.changePhoto} onClick={onPhotoTap}><CameraIcon /></button>
  </div>;
}

/**
 * Renders the share highlight host experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
export default function ShareHighlightHost() {
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const [updateMyProfile] = useMutation(UPDATE_MY_PROFILE, { refetchQueries: [{ query: GET_MY_PROFILE }] });
  const [highlight, setHighlight] = useState(null);
  const [shareTitle, setShareTitle] = useState(null);
  const [format, setFormat] = useState('post');
  const [includeReview, setIncludeReview] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState('');
  const [mediaSource, setMediaSource] = useState('fallback');
  const [customPhotoUrl, setCustomPhotoUrl] = useState('');
  const [showPhotoSources, setShowPhotoSources] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [sharePreferences, setSharePreferences] = useState(() => resolveExternalSharingPreferences().shareHighlights);
  const [showSharedDetails, setShowSharedDetails] = useState(false);
  const [preferencesSaved, setPreferencesSaved] = useState(false);
  const [adjustments, setAdjustments] = useState({ post: { ...DEFAULT_ADJUSTMENT }, story: { ...DEFAULT_ADJUSTMENT } });
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const canShareFiles = useMemo(supportsNativeFileSharing, []);

  const profile = profileData?.getMyProfile;
  const savedSharePreferences = useMemo(
    () => resolveExternalSharingPreferences(profile?.externalSharingPreferences).shareHighlights,
    [profile?.externalSharingPreferences],
  );

  useEffect(() => {
    /**
     * Performs the requested operation.
     *
     * @param {*} event - Browser or React event that triggered the action.
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const open = (event) => {
      setHighlight(event.detail);
      setShareTitle(null);
      setFormat('post');
      setIncludeReview(Boolean(savedSharePreferences.includeReviews));
      setError('');
      setMediaSource(event.detail?.mediaUrl ? 'entry' : 'fallback');
      setCustomPhotoUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return '';
      });
      setAdjustments({ post: { ...DEFAULT_ADJUSTMENT }, story: { ...DEFAULT_ADJUSTMENT } });
      setShowPhotoSources(false);
      setShowPhotoEditor(false);
      setShowSharedDetails(false);
      setPreferencesSaved(false);
      setSharePreferences({ ...savedSharePreferences });
    };
    window.addEventListener('hobtalk:share-highlight', open);
    return () => window.removeEventListener('hobtalk:share-highlight', open);
  }, [savedSharePreferences]);

  useEffect(() => () => {
    if (customPhotoUrl) URL.revokeObjectURL(customPhotoUrl);
  }, [customPhotoUrl]);

  const style = useMemo(() => getHobbyMediaStyle(highlight?.hobby), [highlight?.hobby]);
  const filteredHighlight = useMemo(
    () => highlight ? filterHighlightForSharing(highlight, sharePreferences, profile?.displayName || '') : null,
    [highlight, sharePreferences, profile?.displayName],
  );
  const canCustomizeTitle = sharePreferences.showPlaceNames || !['traveling', 'hiking'].includes(filteredHighlight?.hobby);
  const shareHighlight = useMemo(() => {
    if (!filteredHighlight) return null;
    return shareTitle?.trim() && canCustomizeTitle ? { ...filteredHighlight, title: shareTitle.trim() } : filteredHighlight;
  }, [filteredHighlight, shareTitle, canCustomizeTitle]);
  const titleNeedsShortening = useMemo(() => {
    if (!shareHighlight || typeof document === 'undefined') return false;
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return false;
    const isStory = format === 'story';
    return fitShareTitle(context, shareHighlight.title, FORMATS[format].width - (isStory ? 156 : 144), isStory ? 70 : 61, isStory ? 44 : 43, 4).truncated;
  }, [shareHighlight?.title, format]);
  if (!highlight) return null;

  const mediaState = mediaSource === 'custom' && customPhotoUrl
    ? { source: 'custom', url: customPhotoUrl }
    : mediaSource === 'entry' && filteredHighlight.mediaUrl
      ? { source: 'entry', url: filteredHighlight.mediaUrl }
      : { source: 'fallback', url: '' };
  const adjustment = adjustments[format];

  /**
   * Performs the requested operation.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const close = () => {
    setHighlight(null);
    setShowPhotoSources(false);
    setShowPhotoEditor(false);
    setCustomPhotoUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return '';
    });
  };

  /**
   * Performs photo.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const selectPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      if (file) setError(strings.photoFailed);
      return;
    }
    const url = URL.createObjectURL(file);
    setCustomPhotoUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return url;
    });
    setMediaSource('custom');
    setAdjustments({ post: { ...DEFAULT_ADJUSTMENT }, story: { ...DEFAULT_ADJUSTMENT } });
    setShowPhotoSources(false);
    setShowPhotoEditor(true);
    setError('');
  };

  /**
   * Updates adjustment.
   *
   * @param {string} key - key value.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateAdjustment = (key, value) => {
    setAdjustments((current) => ({
      ...current,
      [format]: { ...current[format], [key]: Number(value) },
    }));
  };

  /**
   * Performs the prepare operation.
   *
   * @param {*} share - share value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const prepare = async (share) => {
    setIsPreparing(true);
    setError('');
    try {
      const blob = await renderHighlight(shareHighlight, format, includeReview, mediaState, adjustment);
      const file = new File([blob], `hobtalk-${shareHighlight.hobby}-highlight.png`, { type: 'image/png' });
      if (share && navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: shareHighlight.title, text: `${strings.footer} · https://${strings.website}` });
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (nextError) {
      if (nextError?.name !== 'AbortError') setError(strings.shareFailed);
    } finally {
      setIsPreparing(false);
    }
  };

  /**
   * Performs share defaults.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const saveShareDefaults = async () => {
    setError('');
    try {
      await updateMyProfile({ variables: { input: { externalSharingPreferences: { shareHighlights: sharePreferences } } } });
      setPreferencesSaved(true);
    } catch {
      setError(strings.shareFailed);
    }
  };

  return (
    <AccessibleModal
      className="insights-share-modal share-highlight-modal share-highlight-modal-v2"
      overlayClassName="insights-share-overlay"
      labelledBy="share-highlight-title"
      onClose={close}
      closeOnEscape={!showPhotoSources}
    >
      <header className="insights-share-modal-header">
        <div><p className="eyebrow">{style.label}</p><h2 id="share-highlight-title" tabIndex="-1" data-modal-initial-focus>{strings.title}</h2></div>
        <button type="button" className="icon-action" aria-label={strings.close} onClick={close}>×</button>
      </header>

      <div className="insights-share-controls share-highlight-controls">
        <div><span>{strings.layout}</span><div className="segmented-control insights-layout-selector" role="radiogroup" aria-label={strings.layout}>
          {Object.keys(FORMATS).map((value) => <button type="button" role="radio" aria-checked={format === value} key={value} className={format === value ? 'active' : ''} onClick={() => setFormat(value)}><span><strong>{value === 'post' ? strings.portrait : strings.fullScreen}</strong><small>{value === 'post' ? strings.portraitRatio : strings.fullScreenRatio}</small></span></button>)}
        </div></div>
      </div>

      <div className="insights-share-preview-wrap share-highlight-preview-wrap">
        <div className={`insights-share-preview-stage share-highlight-preview-stage ${format}`}>
          <ShareHighlightPreview highlight={shareHighlight} format={format} includeReview={includeReview} mediaState={mediaState} adjustment={adjustment} style={style} onPhotoTap={() => setShowPhotoSources(true)} />
        </div>
      </div>

      {canCustomizeTitle && (titleNeedsShortening || shareTitle !== null) && <label className="share-highlight-title-edit">
        <span>{strings.shareTitle}</span>
        <input type="text" value={shareTitle ?? filteredHighlight.title} onChange={(event) => setShareTitle(event.target.value)} aria-describedby={titleNeedsShortening ? 'share-highlight-title-help' : undefined} />
        {titleNeedsShortening && <small id="share-highlight-title-help">{strings.shortenTitle}</small>}
      </label>}

      {showPhotoEditor && mediaState.url && <section className="share-highlight-inline-editor" aria-label={strings.editPhoto}>
        <header><strong>{strings.editPhoto}</strong><button type="button" className="icon-action" aria-label={strings.close} onClick={() => setShowPhotoEditor(false)}>×</button></header>
        <label>{strings.zoom}<input type="range" min="1" max="2.5" step="0.01" value={adjustment.zoom} onChange={(event) => updateAdjustment('zoom', event.target.value)} /></label>
        <label>{strings.horizontalPosition}<input type="range" min="-1" max="1" step="0.01" value={adjustment.horizontal} onChange={(event) => updateAdjustment('horizontal', event.target.value)} /></label>
        <label>{strings.verticalPosition}<input type="range" min="-1" max="1" step="0.01" value={adjustment.vertical} onChange={(event) => updateAdjustment('vertical', event.target.value)} /></label>
        <button type="button" className="btn-secondary" onClick={() => setAdjustments((current) => ({ ...current, [format]: { ...DEFAULT_ADJUSTMENT } }))}>{strings.reset}</button>
      </section>}

      <button type="button" className="share-highlight-details-toggle" onClick={() => setShowSharedDetails((value) => !value)} aria-expanded={showSharedDetails}>
        <span><strong>{strings.sharedDetails}</strong><small>{strings.sharedDetailsSummary}</small></span><b aria-hidden="true">{showSharedDetails ? '−' : '+'}</b>
      </button>
      {showSharedDetails && <section className="share-highlight-shared-details">
        {externalSharePreferenceOptions.map((option) => (
          <label key={option.key}><span>{option.label}</span><input type="checkbox" checked={Boolean(sharePreferences[option.key])} onChange={(event) => { setPreferencesSaved(false); setSharePreferences((current) => ({ ...current, [option.key]: event.target.checked })); if (option.key === 'includeReviews') setIncludeReview(event.target.checked); }} /></label>
        ))}
        <button type="button" className="btn-secondary" onClick={saveShareDefaults}>{preferencesSaved ? strings.defaultSaved : strings.saveDefaults}</button>
      </section>}

      {error && <p className="error-copy">{error}</p>}
      <div className={`insights-share-export-actions ${canShareFiles ? '' : 'single'}`.trim()}>
        {canShareFiles && <button type="button" className="btn-primary" disabled={isPreparing || titleNeedsShortening} onClick={() => prepare(true)}>{isPreparing ? strings.preparing : strings.share}</button>}
        <button type="button" className={canShareFiles ? 'btn-secondary' : 'btn-primary'} disabled={isPreparing || titleNeedsShortening} onClick={() => prepare(false)}>{isPreparing ? strings.preparing : strings.download}</button>
      </div>

      <input ref={photoInputRef} hidden type="file" accept="image/*" onChange={selectPhoto} />
      <input ref={cameraInputRef} hidden type="file" accept="image/*" capture="environment" onChange={selectPhoto} />
      {showPhotoSources && <AccessibleModal className="share-photo-dialog" overlayClassName="nested-accessible-modal-overlay" labelledBy="share-photo-source-title" onClose={() => setShowPhotoSources(false)}>
        <header><h3 id="share-photo-source-title">{strings.photoSource}</h3><button type="button" className="icon-action" aria-label={strings.close} onClick={() => setShowPhotoSources(false)}>×</button></header>
        <button type="button" onClick={() => cameraInputRef.current?.click()}>{strings.takePhoto}</button>
        <button type="button" onClick={() => photoInputRef.current?.click()}>{strings.choosePhoto}</button>
        {mediaState.url && <button type="button" onClick={() => { setShowPhotoSources(false); setShowPhotoEditor(true); }}>{strings.editPhoto}</button>}
        {mediaSource !== 'fallback' && <button type="button" className="destructive" onClick={() => { setMediaSource('fallback'); setShowPhotoSources(false); }}>{strings.removePhoto}</button>}
        <p className="share-photo-note">{strings.photoPrivacy}<br />{strings.photoPermission}</p>
      </AccessibleModal>}
    </AccessibleModal>
  );
}
